import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { func, oneOfType, shape, string, bool, object } from 'prop-types';
import classNames from 'classnames';
import { propTypes, LISTING_STATE_CLOSED } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { getFirstStringPart } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { PrimaryButton, InlineTextButton, ExternalLink, ExpandableBio, Icon } from '..';

import css from './ShowPanel.module.css';

const DESCRIPTION_MAX_LENGTH = 180;

const { Money } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price.amount && price.currency) {
    const formattedPrice = formatMoney(intl, new Money(price.amount, price.currency));
    return { formattedPrice, priceTitle: formattedPrice };
  }

  return {};
};

const listingInfoItem = (itemIcon, itemName) => {
  return itemName ? (
    <div className={css.listingInfoItem}>
      {itemIcon}
      <span>{itemName}</span>
    </div>
  ) : null;
};

const ShowPanel = props => {
  const {
    rootClassName,
    className,
    intl,
    listing,
    showListingInProgress,
    listingFields,
    isOwnListing,
    onOpenDescriptionModal,
    onJoinWaitlist,
    joinWaitlistInProgress,
    joinWaitlistError,
    onLeaveWaitlist,
    leaveWaitlistInProgress,
    leaveWaitlistError,
    currentUser,
    history,
    location,
    onOpenTourSchedulesModal,
  } = props;

  if (showListingInProgress) {
    return null;
  }

  const price = listing.attributes.price;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const availabilityStatus = listing.attributes.publicData?.availabilityStatus;
  const isAvailable = availabilityStatus === 'available';
  const isCollectingWaitlist = availabilityStatus === 'collectWaitlist';

  const showTouringLinkButton =
    hasListingState && !isClosed && listingFields.touringLink && isAvailable;
  const showWaitlistButton = hasListingState && !isClosed && isCollectingWaitlist;

  // Check if current user is already in waitlist
  const waitlistUserIds = listing.attributes.metadata?.waitlistUserIds || [];
  const currentUserId = currentUser?.id?.uuid;
  const isUserInWaitlist = currentUserId && waitlistUserIds.includes(currentUserId);
  const waitlistCount = waitlistUserIds.length;

  const handleJoinWaitlist = () => {
    if (!currentUser) {
      // Redirect unauthorized users to login page
      const state = {
        from: `${location.pathname}${location.search}${location.hash}`,
      };
      const routes = routeConfiguration();
      history.push(createResourceLocatorString('LoginPage', routes), state);
      return;
    }

    if (onJoinWaitlist && currentUser) {
      onJoinWaitlist(listing.id);
    }
  };

  const handleLeaveWaitlist = () => {
    if (onLeaveWaitlist && currentUser) {
      onLeaveWaitlist(listing.id);
    }
  };

  // `conflict`: the list is full, or the page closed or stopped collecting since it loaded
  const waitlistFailureId =
    joinWaitlistError?.code === 'conflict'
      ? 'PagePanel.waitlistNotOpen'
      : joinWaitlistError || leaveWaitlistError
      ? 'PagePanel.waitlistUpdateFailed'
      : null;

  // Outside the buttons' conditions: someone on the list can leave a page that has closed or
  // stopped collecting, as the server lets them
  const waitlistFooter = (
    <>
      {waitlistFailureId ? (
        <p className={css.waitlistRefusal} role="alert">
          <FormattedMessage id={waitlistFailureId} />
        </p>
      ) : null}
      {isUserInWaitlist ? (
        <InlineTextButton
          className={css.leaveWaitlistButton}
          onClick={handleLeaveWaitlist}
          inProgress={leaveWaitlistInProgress}
        >
          <FormattedMessage id="PagePanel.leaveWaitlistButton" />
        </InlineTextButton>
      ) : null}
    </>
  );

  const { formattedPrice, priceTitle } = priceData(price, intl);

  const showDates = !(listing.attributes.publicData?.projectDates?.[0] === 'tbc');

  const classes = classNames(rootClassName || css.root, className);
  const bookButtonClasses = classNames(css.bookButton, {
    [css.ownBookButton]: isOwnListing,
    [css.inWaitlistButton]: isUserInWaitlist,
  });

  // Check if tour schedules exist
  const tourSchedules = listing?.attributes?.publicData?.tourSchedules || [];
  const hasTourSchedules = tourSchedules.length > 0;

  return (
    <div className={classes}>
      {hasTourSchedules && onOpenTourSchedulesModal ? (
        <InlineTextButton onClick={onOpenTourSchedulesModal}>
          <Icon name="calendar" size={24} className={css.calendarIcon} />
        </InlineTextButton>
      ) : hasTourSchedules ? (
        <Icon name="calendar" size={24} className={css.calendarIcon} />
      ) : null}
      <div className={css.desktopPanel}>
        <div className={css.desktopHeader}>
          <p className={css.priceText}>
            <FormattedMessage id="PagePanel.priceText" />
          </p>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice ? formattedPrice : <FormattedMessage id="PagePanel.noPrice" />}
          </div>
          {listingFields.showDate && showDates ? (
            <p className={css.dates}>{listingFields.showDate}</p>
          ) : null}
        </div>
        <div className={css.desktopContent}>
          {listingFields.status && listingFields.address ? (
            <p className={css.tour}>
              <FormattedMessage
                id="PagePanel.tour"
                values={{
                  status: listingFields.status,
                  name: (
                    <InlineTextButton className={css.tourName} disabled>
                      {getFirstStringPart(listingFields.address)}
                    </InlineTextButton>
                  ),
                }}
              />
            </p>
          ) : null}
          <div className={css.description}>
            <ExpandableBio
              bio={listingFields.description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              onReadMore={onOpenDescriptionModal}
            />
          </div>
          <div className={css.listingInfo}>
            {listingInfoItem(<Icon name="wand" size={24} />, listingFields.category)}
            {listingInfoItem(<Icon name="genre" size={24} />, listingFields.genre)}
            {listingInfoItem(<Icon name="time" size={24} />, listingFields.length)}
            {listingInfoItem(<Icon name="restricted" size={24} />, listingFields.age)}
          </div>
        </div>
        {showTouringLinkButton ? (
          <ExternalLink className={bookButtonClasses} href={listingFields.touringLink}>
            <FormattedMessage id="PagePanel.ctaButtonMessage" />
          </ExternalLink>
        ) : showWaitlistButton ? (
          <>
            <PrimaryButton
              className={bookButtonClasses}
              onClick={handleJoinWaitlist}
              inProgress={joinWaitlistInProgress}
              disabled={isUserInWaitlist || joinWaitlistInProgress || isOwnListing}
            >
              {isUserInWaitlist ? (
                <FormattedMessage
                  id="PagePanel.inWaitlistButton"
                  values={{ count: waitlistCount }}
                />
              ) : waitlistCount > 0 ? (
                <FormattedMessage
                  id="PagePanel.joinWaitlistButton"
                  values={{ count: waitlistCount }}
                />
              ) : (
                <FormattedMessage id="PagePanel.joinWaitlistButtonWithoutCount" />
              )}
            </PrimaryButton>
          </>
        ) : isClosed ? (
          <PrimaryButton className={bookButtonClasses} disabled>
            <FormattedMessage id="PagePanel.closedListingButtonText" />
          </PrimaryButton>
        ) : null}
        {waitlistFooter}
      </div>
      <div className={css.openBookingForm}>
        <div className={css.priceContainer}>
          <div className={css.priceValue} title={priceTitle}>
            <FormattedMessage id="PagePanel.mobilePrice" values={{ price: formattedPrice }} />
          </div>
        </div>
        {showTouringLinkButton ? (
          <ExternalLink className={css.bookButton} href={listingFields.touringLink}>
            <FormattedMessage id="PagePanel.ctaButtonMessage" />
          </ExternalLink>
        ) : showWaitlistButton ? (
          <>
            <PrimaryButton
              className={bookButtonClasses}
              onClick={handleJoinWaitlist}
              inProgress={joinWaitlistInProgress}
              disabled={isUserInWaitlist || joinWaitlistInProgress || isOwnListing}
            >
              {isUserInWaitlist ? (
                <FormattedMessage
                  id="PagePanel.inWaitlistButton"
                  values={{ count: waitlistCount }}
                />
              ) : waitlistCount > 0 ? (
                <FormattedMessage
                  id="PagePanel.joinWaitlistButton"
                  values={{ count: waitlistCount }}
                />
              ) : (
                <FormattedMessage id="PagePanel.joinWaitlistButtonWithoutCount" />
              )}
            </PrimaryButton>
          </>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="PagePanel.closedListingButtonText" />
          </div>
        ) : null}
        {waitlistFooter}
      </div>
    </div>
  );
};

ShowPanel.defaultProps = {
  rootClassName: null,
  className: null,
  listingFields: null,
  showListingInProgress: false,
  isOwnListing: false,
  onOpenDescriptionModal: null,
  onJoinWaitlist: null,
  joinWaitlistInProgress: false,
  joinWaitlistError: null,
  onLeaveWaitlist: null,
  leaveWaitlistInProgress: false,
  leaveWaitlistError: null,
  currentUser: null,
  onOpenTourSchedulesModal: null,
};

ShowPanel.propTypes = {
  rootClassName: string,
  className: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing, object]),
  showListingInProgress: bool.isRequired,
  listingFields: object,
  isOwnListing: bool.isRequired,
  onOpenDescriptionModal: func.isRequired,
  onJoinWaitlist: func,
  joinWaitlistInProgress: bool,
  joinWaitlistError: object,
  onLeaveWaitlist: func,
  leaveWaitlistInProgress: bool,
  leaveWaitlistError: object,
  currentUser: propTypes.currentUser,
  onOpenTourSchedulesModal: func,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    pathname: string.isRequired,
    search: string.isRequired,
    hash: string.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(ShowPanel);
